import { ReactNode, useMemo } from 'react';
import { CheckboxRecaptchaResult, useCheckboxRecaptcha } from './useCheckboxRecaptcha';
import { ScoreRecaptchaResult, useScoreRecaptcha } from './useScoreRecaptcha';
import { useAppSelector } from '../../../stores/redux.store';
import {
  captureRecaptchaMessage,
  captureTaggedException,
} from '../../../utilities/analytics/error-reporting';
import { RecaptchaCheckboxTheme, RecaptchaConfig } from './types';

export type RecaptchaType = 'score' | 'checkbox' | 'policy';
export type ChallengeKind = 'recaptcha';

export type RecaptchaProps =
  | { type: 'score'; config: RecaptchaConfig }
  | { type: 'checkbox'; config: RecaptchaConfig }
  | { type: 'policy'; config: RecaptchaConfig };

export type ChallengeResponse = {
  challengeKind: ChallengeKind;
  token: string;
  type: RecaptchaType;
};

export type UseRecaptchaResult = {
  fetchToken: () => Promise<ChallengeResponse | null>;
  isLoaded: boolean;
  enabled: boolean;
  errorMessage?: string;
  // checkbox only
  component?: ReactNode;
};

const noOpScoreConfig: RecaptchaConfig = { sitekey: '', pageId: '', enabled: false };
const noOpCheckboxConfig: RecaptchaConfig = {
  sitekey: '',
  pageId: '',
  enabled: false,
  action: '',
};

export type UseRecaptchaProps = {
  pageId: string;
  type: RecaptchaType;
  enabledProp?: boolean;
  action: string;
  checkboxTheme?: RecaptchaCheckboxTheme;
};
export const useRecaptcha = (props: UseRecaptchaProps): UseRecaptchaResult => {
  const { pageId, type, enabledProp = true, action, checkboxTheme } = props;
  const { config } = useCreateRecaptchaProps(pageId, type, enabledProp, action, checkboxTheme);

  const isScore = type === 'score';
  const isCheckbox = type === 'checkbox';
  const scoreRecaptcha = useScoreRecaptcha(isScore ? config : noOpScoreConfig);

  const checkboxRecaptcha = useCheckboxRecaptcha(isCheckbox ? config : noOpCheckboxConfig);

  return useMemo(
    () => (isScore ? handleScore(scoreRecaptcha, action) : handleCheckbox(checkboxRecaptcha)),
    [isScore, scoreRecaptcha, checkboxRecaptcha, action]
  );
};

export function useCreateRecaptchaProps(
  pageId: string,
  type: RecaptchaType,
  enabledProp: boolean,
  action?: string,
  checkboxTheme?: RecaptchaCheckboxTheme
): RecaptchaProps {
  const captchaEnabledByConfig = useAppSelector(state => state.appConfig.recaptchaEnable);
  const scoreSitekey = useAppSelector(state => state.appConfig.recaptchaEnterpriseScoreKeySite);
  const checkboxSitekey = useAppSelector(
    state => state.appConfig.recaptchaEnterpriseCheckboxKeySite
  );

  const enabled = captchaEnabledByConfig && enabledProp;

  const commonConfig = {
    pageId,
    enabled,
    action,
  };

  const scoreConfig: RecaptchaConfig = {
    ...commonConfig,
    sitekey: scoreSitekey,
    displayBadge: false,
  };

  const checkboxConfig: RecaptchaConfig = {
    ...commonConfig,
    sitekey: checkboxSitekey,
    action: action ?? 'unknown',
    ...(checkboxTheme && { theme: checkboxTheme }),
  };

  const policyConfig: RecaptchaConfig = {
    ...commonConfig,
    sitekey: '', // Update when adding support for policy based recaptcha (aka unicaptcha)
  };

  switch (type) {
    case 'score':
      return {
        type,
        config: scoreConfig,
      };
    case 'checkbox':
      return {
        type,
        config: checkboxConfig,
      };
    case 'policy':
      return {
        type,
        config: policyConfig,
      };

    default:
      captureTaggedException(new Error(`Unexpected captcha type: ${type}`), {
        capabilities: action === 'signup' ? ['capability-group--signup'] : [],
        tags: { recaptcha: 'unexpected_captcha_type' },
      });

      return {
        type: type as RecaptchaType,
        config: {
          ...commonConfig,
          sitekey: '',
          enabled: false,
        },
      } as RecaptchaProps;
  }
}

const handleCheckbox = (checkboxRecaptcha: CheckboxRecaptchaResult): UseRecaptchaResult => {
  const wrapToken = () => {
    return Promise.resolve({
      token: checkboxRecaptcha.token || '',
      type: 'checkbox' as RecaptchaType,
      challengeKind: 'recaptcha' as ChallengeKind,
    });
  };

  const isLoaded = checkboxRecaptcha.isLoaded;
  const enabled = checkboxRecaptcha?.enabled;
  const errorMessage = checkboxRecaptcha?.error?.message ?? undefined;
  const component = checkboxRecaptcha.component;

  const onFetchToken = async () => {
    const response = await wrapToken();

    // TODO once the flag is cleaned up, the type of response will always be truthy so we can change the check to response.token === ''
    if ((!response || !response.token) && enabled) {
      captureRecaptchaMessage(
        errorMessage ?? 'no token returned from checkbox recaptcha',
        {},
        'no_token_checkbox'
      );
    }

    return response;
  };

  return {
    fetchToken: onFetchToken,
    isLoaded: isLoaded || false,
    enabled: enabled || false,
    errorMessage,
    component,
  };
};

const handleScore = (scoreRecaptcha: ScoreRecaptchaResult, action: string): UseRecaptchaResult => {
  const fetchAndWrapToken = (action: string) =>
    scoreRecaptcha.fetchRecaptchaToken(action).then(token => {
      return {
        token: token || '',
        type: 'score' as RecaptchaType,
        challengeKind: 'recaptcha' as ChallengeKind,
      };
    });
  const isLoaded = scoreRecaptcha.isLoaded;
  const enabled = scoreRecaptcha?.enabled;
  const errorMessage = scoreRecaptcha?.loadRecaptchaErrorReason ?? undefined;

  const onFetchToken = async () => {
    const response = await fetchAndWrapToken(action);

    // TODO once the flag is cleaned up, the type of response will always be truthy so we can change the check to response.token === ''
    if ((!response || !response.token) && enabled) {
      captureRecaptchaMessage(
        errorMessage ?? 'no token returned from score recaptcha',
        {},
        'no_token_score'
      );
    }
    return response;
  };

  return {
    fetchToken: onFetchToken,
    isLoaded: isLoaded || false,
    enabled: enabled || false,
    errorMessage,
  };
};
